import { Injectable } from '@angular/core';
import { forEach } from '@angular/router/src/utils/collection';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'angular2-toaster/src/toaster.service';
import { AppConfig } from 'app/app.config';
import { TagItem } from 'app/components/common/tag/tag.component';
import { AuthService } from 'app/services/auth.service';
import { Clock } from 'app/services/clock';
import { DevicesGroupRepository } from 'app/services/database/devicesGroup.repository';
import { PresentationRepository } from 'app/services/database/presentation.repository';
import { Repository } from 'app/services/database/repository';
import { UserRepository } from 'app/services/database/user.repository';
import { DeviceService } from 'app/services/device.service';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { DevicesGroupModel } from 'app/views/devices/models/devicesGroup.model';
import { unescape } from 'querystring';
import * as url from 'url';

@Injectable()
export class DevicesGroupService {

    private logger: Logger;

    constructor(private appConfig: AppConfig,
                private devicesGroupRepository: DevicesGroupRepository,
                private presentationRepository: PresentationRepository,
                private userRepository: UserRepository,
                private auth: AuthService,
                private deviceService: DeviceService,
                private translate: TranslateService,
                private toasterService: ToasterService,
                private clock: Clock,
                private loggerFactory: LoggerFactory
    ) {
        this.logger = loggerFactory.create('DevicesGroupService');
    }

    public async devicesGroupExists(devicesGroupId: string): Promise<boolean> {
        return await this.devicesGroupRepository.exists(devicesGroupId);
    }

    public async getDevicesGroup(id: string): Promise<DevicesGroupModel> {
        return await this.devicesGroupRepository.get(id);
    }

    public async getUserDevicesGroups(userId: string): Promise<DevicesGroupModel[]> {
        let user = await this.userRepository.get(userId);
        let devicesGroups: DevicesGroupModel[] = [];
        if (user.devicesGroups && user.devicesGroups.length !== 0) {
            for (let devicesGroupId of user.devicesGroups) {
                try {
                    let devicesGroup = await this.devicesGroupRepository.get(devicesGroupId);
                    devicesGroups.push(devicesGroup);
                } catch (err) {
                    this.logger.error(`An error occured while getting the user '${userId}' deviceGroup '${devicesGroupId}'.`);
                }
            }
        }
        return devicesGroups;
    }

    public async saveDevicesGroup(devicesGroup: DevicesGroupModel): Promise<string> {

        if (!devicesGroup.name) {
            throw new Error('Cannot save a devicesGroup without a name');
        }
        if (!devicesGroup.owners) {
            throw new Error('Cannot save a devicesGroup without owners');
        }
        if (!devicesGroup.creationDate) {
            throw new Error('Cannot save a devicesGroup without creation date');
        }

        if (!devicesGroup.id) {
            devicesGroup.id = await this.devicesGroupRepository.generateKey();
        }

        if (!devicesGroup.id) {
            throw new Error('Cannot save a devicesGroup without an id');
        }

        devicesGroup.modificationDate = this.clock.now;
        await this.addDevicesGroupToUser(devicesGroup);
        await this.setDevicesOfGroupOwnerOfSchedule(devicesGroup);

        try {
            await this.devicesGroupRepository.set(devicesGroup.id, devicesGroup);
        } catch (err) {
            this.removeDevicesGroupFromUser(devicesGroup.id);
            throw err;
        }
        return devicesGroup.id;
    }

    private async setDevicesOfGroupOwnerOfSchedule(devicesGroup: DevicesGroupModel) {
        if (devicesGroup.schedule) {
            if (devicesGroup.schedule.defaultPresentationId) {
                await this.addDevicesOfGroupToPresentationOwners(devicesGroup, devicesGroup.schedule.defaultPresentationId);
            }
            if (devicesGroup.schedule.entries) {

                for (let sheduledPres of devicesGroup.schedule.entries) {
                    await this.addDevicesOfGroupToPresentationOwners(devicesGroup, sheduledPres.presentationId);
                }
            }
        }
    }

    private async addDevicesOfGroupToPresentationOwners(devicesGroup: DevicesGroupModel, presentationId: string) {

        let presentation = await this.presentationRepository.get(presentationId);
        if (!presentation.owners) {
            presentation.owners = {};
        }

        let deviceModels = await this.getDevicesOfGroup(devicesGroup.id);
        // Set each device of the group owner of the presentations
        for (let deviceModel of deviceModels) {
            // Set the device as an owner of the presentation
            presentation.owners[deviceModel.id] = true;
        }

        // Update the presentation
        await this.presentationRepository.update(presentation.id, presentation);
    }

    private async addDevicesGroupToUser(devicesGroup: DevicesGroupModel) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);
        let isAlreadyAssigned = false;
        if (!userModel.devicesGroups) {
            userModel.devicesGroups = [];
        } else {
            for (let id of userModel.devicesGroups) {
                if (id === devicesGroup.id) {
                    isAlreadyAssigned = true;
                }
            }
        }
        if (!isAlreadyAssigned) {
            userModel.devicesGroups.push(devicesGroup.id);
            await this.userRepository.update(authUser.uid, userModel);
        }
    }

    private async removeDevicesGroupFromUser(uid: string) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);
        if (userModel.devicesGroups) {
            userModel.devicesGroups = userModel.devicesGroups.filter(obj => obj !== uid);
            await this.userRepository.set(authUser.uid, userModel);
        }
    }

    private async removeGroupFromDevicesOfGroup(id: string) {
        // remove devicesGroup from devices
        let deviceModels = await this.getDevicesOfGroup(id);
        for (let deviceModel of deviceModels) {
            // remove device from group
            deviceModel.groupId = undefined;
            await this.deviceService.saveDevice(deviceModel);
        }
    }

    public async createDevicesGroup() {
        let devicesGroup = new DevicesGroupModel();
        devicesGroup.name = '';
        devicesGroup.creationDate = this.clock.now;
        let user = await this.auth.getCurrentUser();
        devicesGroup.owners = {};
        devicesGroup.owners[user.uid] = true;
        return devicesGroup;
    }

    public async createDevicesGroupFromDevice(device: DeviceModel) {
        try {
            let devicesGroup = await this.createDevicesGroup();
            devicesGroup.name = device.name;
            devicesGroup.schedule = device.schedule;
            await this.saveDevicesGroup(devicesGroup);
            this.toasterService.pop('success', '', 'modules.devices.edit.group-creation-success');
            return devicesGroup;
        } catch (err) {
            this.logger.error(`An error occured while creating group with device '${device.id}'\'s settings`, err);
            this.toasterService.pop('error', '', 'modules.devices.edit.error-creating-group');
            return undefined;
        }
    }

    public async getDevicesOfGroup(groupId: string) {
        let listDevices: DeviceModel[] = [];
        let user = await this.auth.getCurrentUser();
        try {
            let deviceModels = await this.deviceService.getUserDeviceList(user.uid);
            for (let deviceModel of deviceModels) {
                // add device to list if it has the groupId of the group
                if (deviceModel.groupId && deviceModel.groupId === groupId) {
                    listDevices.push(deviceModel);
                }
            }
        } catch (err) {
            this.logger.error(`An error occured while getting devices for user.`, err);
        }
        return listDevices;
    }

    public async deleteDevicesGroup(id: string) {
        await this.removeDevicesGroupFromUser(id);
        await this.removeGroupFromDevicesOfGroup(id);
        // Method call order is critical
        await this.devicesGroupRepository.remove(id);
    }

    public async overrideDevicesSetting(id: string) {
        let group = await this.getDevicesGroup(id);
        let devices = await this.getDevicesOfGroup(id);
        for (let device of devices) {
            try {
                device.schedule = group.schedule;
                await this.deviceService.saveDevice(device);
            } catch (err) {
                this.logger.error('There was an error will overriding device setting.', err);
            }
        }
    }

    public async createDevicesGroupTag(groupId: string, showLabel: boolean = true) {
        if (!groupId) return undefined;

        try {
            let group = await this.getDevicesGroup(groupId);

            if (group) {
                let deviceGroupStr: string;
                if (showLabel) {
                    deviceGroupStr = await this.translate.get('modules.devices.groups.group').toPromise();
                }
                return new TagItem(deviceGroupStr, group.name, 'default');

            }
        } catch (e) {
            this.logger.error(`An error occured while creating group '${groupId}' tag, returning undefined (tag).`, e);
        }
        return undefined;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/devicesGroup.service.ts